import { CloseCircleOutlined } from '@ant-design/icons'
import { useLockFn, useRequest } from 'ahooks'
import { Button, Dropdown, Menu, Modal, Tooltip, message } from 'antd'
import React from 'react'
import { TFunction, useTranslation } from 'react-i18next'

import { CustomerList } from '@/modules/customer/customer-home.component'
import { CustomerTerminateFlowStatus } from '@/modules/customer/customer.schema'
import { DevlopList } from '@/modules/devlop/devlop-home.component'
import { DevlopStatus } from '@/modules/devlop/devlop.schema'
import { DisabledRelationStatus } from '@/modules/production-project/production-detail/production-summary'
import { ProductionList } from '@/modules/production-project/production-home.component'
import { ProductionTerminateFlowStatus } from '@/modules/production-project/production.schema'
import { PpRelation, ResourceType } from '@/schemas'

import { OKRList } from './okr-list.component'
import { PortfolioList } from './portfolio-list.component'
import { ProjectList } from './project-list.component'

interface RelationsCreatorProps {
  direction: 'up' | 'down'
  rootSelfId: string
  selfId: string | number // 被关联者
  type: ResourceType
  targetType: ResourceType // 关联类型
  disabledSelfIds: (string | number)[]
  onSuccess: () => void
}

interface RelationsCreatorDropdownProps {
  direction: 'up' | 'down'
  rootSelfId: string
  selfId: string | number
  type: ResourceType
  onSuccess: () => void
  disabledSelfIds: (string | number)[]
}

export const RelationsCreatorDropdown: React.FC<RelationsCreatorDropdownProps> = ({
  direction,
  rootSelfId,
  selfId,
  type,
  onSuccess,
  disabledSelfIds,
  children,
}) => {
  const { t } = useTranslation()

  return (
    <Dropdown
      trigger={['click']}
      overlay={
        <Menu>
          <RelationsCreator
            disabledSelfIds={disabledSelfIds}
            rootSelfId={rootSelfId}
            selfId={selfId}
            type={type}
            targetType={ResourceType.PROJECT}
            onSuccess={onSuccess}
            direction={direction}
          >
            <Menu.Item key='project'>{t('add projects')}</Menu.Item>
          </RelationsCreator>
          <RelationsCreator
            disabledSelfIds={disabledSelfIds}
            rootSelfId={rootSelfId}
            selfId={selfId}
            type={type}
            targetType={ResourceType.PORTFOLIO}
            onSuccess={onSuccess}
            direction={direction}
          >
            <Menu.Item key='portfolio'>{t('add portfolios')}</Menu.Item>
          </RelationsCreator>
        </Menu>
      }
    >
      {children}
    </Dropdown>
  )
}

export const RelationsCreator: React.FC<RelationsCreatorProps> = ({
  direction,
  rootSelfId,
  selfId,
  type,
  targetType,
  children,
  onSuccess,
  disabledSelfIds,
}) => {
  const { t } = useTranslation()
  const [visible, setVisible] = React.useState(false)

  const { run: runCreate, loading } = useRequest(
    (child) => {
      if (!direction) return
      if (
        direction === 'up' // 向上挂 -- 给其挂父节点
      ) {
        // 接入审批 不直接创建关系  direction === 'up' targetType === ResourceType.CUSTOMER || ResourceType.PRODUCTION || ResourceType.DEVLOP
        if (
          targetType === ResourceType.CUSTOMER ||
          targetType === ResourceType.PRODUCTION ||
          targetType === ResourceType.DEVLOP
        ) {
          return {
            url: '/api/relations/createAudit',
            method: 'post',
            data: {
              selfId,
              type,
              parentSelfId: child.selfId,
              parentType: child.type,
            },
          }
        } else {
          return {
            url: '/api/relations/createPpRelations',
            method: 'post',
            data: {
              rootSelfId,
              ...child,
              children: [{ selfId, type }],
            },
          }
        }
        // direction === 'down'
        // 向下挂 -- 给其挂子节点
      } else {
        if ([ResourceType.CUSTOMER, ResourceType.PRODUCTION, ResourceType.DEVLOP].includes(type)) {
          return {
            url: '/api/relations/createAudit',
            method: 'post',
            data: {
              selfId: child.selfId,
              type: child.type,
              parentSelfId: selfId,
              parentType: type,
            },
          }
        } else {
          return {
            url: '/api/relations/createPpRelations',
            method: 'post',
            data: {
              rootSelfId,
              selfId,
              type,
              children: [child],
            },
          }
        }
      }
    },
    {
      manual: true,
      onSuccess: (data) => {
        const errors = data.errors || []
        if (errors.length) {
          for (const error of errors) {
            const config = getRelationCreatedFailedMessage({ error, onClose: message.destroy, t })
            message.error({
              ...config,
              duration: 0,
            })
          }
          return
        }
        if (
          (direction === 'up' &&
            [ResourceType.CUSTOMER, ResourceType.PRODUCTION, ResourceType.DEVLOP].includes(targetType)) ||
          (direction === 'down' && [ResourceType.CUSTOMER, ResourceType.PRODUCTION, ResourceType.DEVLOP].includes(type))
        ) {
          message.success({
            content: t('add audit success'),
          })
        } else {
          message.success(t('add success'))
        }
        onSuccess?.()
      },
    }
  )

  const runCreatedLocked = useLockFn(runCreate)

  const renderExtras = (type: ResourceType, direction: 'up' | 'down') => (_: any, d: any) => {
    const selfId = d.selfId || d.objectiveId
    let specialDisabled = false
    switch (type) {
      case ResourceType.PROJECT: {
        specialDisabled = d.hasParentRelation
        break
      }
      case ResourceType.PORTFOLIO: {
        if (direction === 'down') {
          specialDisabled = d.hasParentRelation
        }
        break
      }
      case ResourceType.PRODUCTION: {
        specialDisabled = DisabledRelationStatus.concat(ProductionTerminateFlowStatus).includes(d.flowStatus)

        break
      }
      case ResourceType.DEVLOP: {
        specialDisabled = d.status === DevlopStatus.已结项
        break
      }

      case ResourceType.CUSTOMER: {
        specialDisabled = CustomerTerminateFlowStatus.includes(d.flowStatus)
        break
      }
    }

    const disabled = disabledSelfIds.map(String).includes(String(selfId))
    const tooltipVisible =
      (!disabled && specialDisabled && type === ResourceType.PROJECT) ||
      (type === ResourceType.PORTFOLIO && direction === 'down' && specialDisabled)
        ? undefined
        : false
    return (
      <Tooltip visible={tooltipVisible} title={t('associate.warning.children.tooltip')}>
        <Button
          size='small'
          type='link'
          disabled={disabled || specialDisabled}
          onClick={(e) => {
            e.stopPropagation()
            runCreatedLocked({ selfId, type })
          }}
        >
          {disabled ? t('已添加') : t('添加')}
        </Button>
      </Tooltip>
    )
  }

  let title = ''
  let component = null
  switch (targetType) {
    case ResourceType.PROJECT: {
      title = t('add projects')
      component = (
        <ProjectList size='small' extraOperationLoading={loading} renderExtras={renderExtras(targetType, direction)} />
      )

      break
    }
    case ResourceType.PORTFOLIO: {
      title = t('add portfolios')
      component = (
        <PortfolioList
          size='small'
          extraOperationLoading={loading}
          renderExtras={renderExtras(targetType, direction)}
        />
      )
      break
    }
    case ResourceType.OBJECTIVE: {
      title = t('add objectives')
      component = <OKRList extraOperationLoading={loading} renderExtras={renderExtras(targetType, direction)} />
      break
    }
    case ResourceType.CUSTOMER: {
      title = t('add customers')
      component = (
        <CustomerList
          canExport={false}
          isFullWidth={false}
          withParamPersist={false}
          extraOperationLoading={loading}
          renderExtras={renderExtras(targetType, direction)}
        />
      )
      break
    }
    case ResourceType.PRODUCTION: {
      title = t('add productions')
      component = (
        <ProductionList
          canExport={false}
          isFullWidth={false}
          withParamPersist={false}
          extraOperationLoading={loading}
          renderExtras={renderExtras(targetType, direction)}
        />
      )
      break
    }
    case ResourceType.DEVLOP: {
      title = t('add devlops')
      component = (
        <DevlopList
          canExport={false}
          isFullWidth={false}
          withParamPersist={false}
          extraOperationLoading={loading}
          renderExtras={renderExtras(targetType, direction)}
        />
      )
      break
    }
  }

  return (
    <>
      {React.cloneElement(children as any, {
        onClick: () => setVisible(true),
      })}
      <Modal
        centered
        width='80vw'
        getContainer={document.body}
        title={title}
        footer={false}
        visible={visible}
        onOk={() => setVisible(false)}
        onCancel={(e) => {
          e.stopPropagation()
          setVisible(false)
        }}
      >
        {component}
      </Modal>
    </>
  )
}

export const getRelationCreatedFailedMessage = ({
  error,
  onClose,
  t,
}: {
  error: {
    info: {
      exist: { selfId: string; name: string }
      resource: { selfId: string; name: string }
      existRelation: PpRelation
    }
  }
  t: TFunction
  onClose: (key: string) => void
}) => {
  const exist = error.info.exist
  const resource = error.info.resource
  const key = `${resource.selfId}-${exist.selfId}`
  const existRelation = error.info.existRelation

  const childName = `${t(`${ResourceType[existRelation.type]}`)}: ${resource.name}`
  const parentName = `${t(`${ResourceType[existRelation.parentType]}`)}: ${exist.name}`
  const errMessage = (
    <span>
      <span>{t('relation.add.error.message.part1')}</span>
      <a
        target='_blank'
        rel='noopener noreferrer'
        href={`${location.origin}/${ResourceType[existRelation.type].toLowerCase()}/${resource.selfId}`}
      >
        {childName}
      </a>
      <span>{t('relation.add.error.message.part2')}</span>
      <a
        target='_blank'
        rel='noopener noreferrer'
        href={`${location.origin}/${ResourceType[existRelation.parentType].toLowerCase()}/${exist.selfId}`}
      >
        {parentName}
      </a>
      <span>{t('relation.add.error.message.part3')}</span>
      <span>{t('relation.add.error.message.part4')}</span>

      <CloseCircleOutlined className='ml-2' onClick={() => onClose(key)}>
        x
      </CloseCircleOutlined>
    </span>
  )

  return { content: errMessage, key }
}
